import { FC, useEffect, useState } from 'react';
import {
  Button,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  InputLabel,
  FormControl,
  ListItemSecondaryAction,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { KeyedMutator } from 'swr';
import { useAbility } from '@casl/react';
import palette from '../../theme/palette';
import { User } from '../../types/User';
import Rk9Api from '../../dataServices/Rk9Api';
import { PUT } from '../../constants/requests';
import { AbilityContext } from '../../context/AbilityContext';

interface ClientProps {
  client: User;
  mutate: KeyedMutator<User[]>;
  openDeleteConfirmation: () => void;
  openResetPasswordConfirmation: () => void;
  setClientToDeleteOrResetPassword: (client: User | null) => void;
}

export const Client: FC<ClientProps> = (props) => {
  const {
    client,
    mutate,
    openDeleteConfirmation,
    openResetPasswordConfirmation,
    setClientToDeleteOrResetPassword,
  } = props;
  const [editMode, setEditMode] = useState(false);
  const [newRole, setNewRole] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const ability = useAbility(AbilityContext);

  useEffect(() => {
    if (client && client.role) setNewRole(client.role);
  }, [client]);

  const updateClientRole = async (e: any) => {
    const changedRole = e.target.value;
    setNewRole(changedRole);
    if (client && client.id && changedRole && ability.can('update', 'All')) {
      const updatedClient = { ...client };
      updatedClient.role = changedRole;

      await Rk9Api(PUT, `/users/admin/${client.id}`, updatedClient)
        .then(() => {
          enqueueSnackbar("Client's information was updated!", {
            persist: false,
            variant: 'success',
          });
        })
        .catch(() =>
          enqueueSnackbar(
            "There was a problem updating the client's information. Please let someone know!",
            {
              persist: false,
              variant: 'error',
            },
          ),
        );
      await mutate();
      setEditMode(false);
    }
  };

  const showDeleteConfirmation = () => {
    if (client) {
      setClientToDeleteOrResetPassword(client);
      openDeleteConfirmation();
    }
  };

  const showResetPasswordConfirmation = () => {
    if (client) {
      setClientToDeleteOrResetPassword(client);
      openResetPasswordConfirmation();
    }
  };

  return (
    <ListItem style={{ display: 'flex', justifyContent: 'space-between' }}>
      <ListItemAvatar>
        <Avatar
          style={{
            backgroundColor: palette.paper.primary,
            color: palette.text.primary,
          }}
        >
          <AccountCircleIcon style={{ fontSize: '3rem' }} />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        style={{ width: '35%' }}
        primary={`${client.name} - (${client.username})`}
        secondary={client.dogName2 ? `${client.dogName} & Friends` : client.dogName}
      />
      <ListItemText style={{ width: '30%' }}>
        {!editMode && `Role: ${client.role.toUpperCase()}`}
        {editMode && (
          <FormControl>
            <InputLabel>Role</InputLabel>
            <Select
              value={newRole}
              onChange={async (role) => {
                if (role) await updateClientRole(role);
              }}
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="client">Client</MenuItem>
              <MenuItem value="guest">Guest</MenuItem>
            </Select>
          </FormControl>
        )}
      </ListItemText>
      <ListItemText style={{ display: 'flex', width: '30%' }}>
        <Button
          size="small"
          variant="outlined"
          style={{
            borderColor: palette.button.primary,
            color: palette.button.primary,
            marginRight: '10px',
          }}
          disabled={!ability.can('update', 'All')}
          onClick={showResetPasswordConfirmation}
        >
          Reset Password
        </Button>
        <Button
          size="small"
          variant="outlined"
          style={{
            borderColor: palette.button.primary,
            color: palette.button.primary,
          }}
          disabled={!ability.can('update', 'All')}
          onClick={() => setEditMode(!editMode)}
        >
          {editMode ? 'Cancel' : 'Edit'}
        </Button>
      </ListItemText>
      <ListItemSecondaryAction style={{ display: 'flex', width: '5%' }}>
        <IconButton disabled={!ability.can('delete', 'All')} onClick={showDeleteConfirmation}>
          <CloseIcon />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

export default Client;
